import type { APIConfig, ThemeConfig, ExportConfig, VoiceConfig, UILabels } from '../types/chat';
import { environment } from '../../../../environments/environment';

/**
 * Default API Configuration
 * 
 * ⚠️ CUSTOMIZE FOR YOUR APP:
 * These are example values. Override with your own:
 * - baseUrl: Your API URL
 * - authPayload: Your credentials
 * - campaignId: Your project ID
 * - agentCode: Your agent code
 * - customQueryParams: Your custom params
 * 
 * Works with ANY app (e-commerce, healthcare, education, banking, etc.)
 */
export const defaultAPIConfig: APIConfig = {
  baseUrl: environment.controlplaneUrl,
  authEndpoint: '/auth/getToken',
  queryEndpoint: '/agent/flow/execute',
  contentTypesEndpoint: '/account/getContentTypes',
  generateContentEndpoint: '/agent/flow/execute',
  authPayload: {
    data: "5b9ba549e2a3e820064f31f6d1e873237223a45716f69802cde3266d7e405f5e85a188226c74b1d0dc130906ab065582bb7f2b4c0961d520612bfba97d15ea6c786a9c39edfc765696a901ca2c899ec2"
  },
  headers: {
    'Content-Type': 'application/json'
  },
  // App-specific configuration (customize for your domain)
  campaignId: '68d7fe2baf140df2048e51a5',
  agentCode: 'QUERY_DOCUMENTS_AND_RESPOND',
  generateAgentCode: 'GENERATE_CONTENT_FROM_CONVERSATION',
  language: ['mr', 'en'],
  customQueryParams: {}
};

/**
 * Default Theme Configuration - Black & White Theme
 */
export const defaultThemeConfig: ThemeConfig = {
  primaryColor: '#000000',
  secondaryColor: '#ffffff',
  backgroundColor: '#ffffff',
  textColor: '#1a1a1a',
  borderColor: '#e5e7eb',
  headerBackground: '#000000',
  buttonStyle: 'rounded'
};

/**
 * Default Export Configuration
 */
export const defaultExportConfig: ExportConfig = {
  enableExport: true,
  enableContentGeneration: true,
  defaultContentTypes: [
    { contentType: 'SOCIAL_POST_LINKEDIN', contentTypeDescription: 'LinkedIn Post', class: 'Semi Formal' },
    { contentType: 'SOCIAL_POST_TWITTER', contentTypeDescription: 'Twitter Post', class: 'Semi Formal' },
    { contentType: 'SOCIAL_POST_FACEBOOK', contentTypeDescription: 'Facebook Post', class: 'Semi Formal' },
    { contentType: 'BLOG_POST', contentTypeDescription: 'Blog Post', class: 'Formal' },
    { contentType: 'EMAIL', contentTypeDescription: 'Email', class: 'Formal' },
    { contentType: 'SHORT_MESSAGE', contentTypeDescription: 'Short Message', class: 'Casual' }
  ],
  brandDetails: {
    website: '',
    industryKeywords: '',
    targetAudience: [],
    brandVoice: ''
  }
};

/**
 * Default Voice Configuration for Gemini
 */
export const defaultVoiceConfig: VoiceConfig = {
  lang: 'mr-IN',
  rate: 1.0,
  pitch: 1.0,
  volume: 1.0,
  geminiApiKey: 'AIzaSyARmXbAQyDPfT-VovuIJdjXrB8PYLbSqwo',
  geminiWsUrl: 'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent'
};

/**
 * Default UI Labels Configuration
 */
export const defaultUILabels: UILabels = {
  // Header
  title: 'AI Assistant',
  subtitle: '',
  
  // Input
  placeholder: 'Type your message...',
  sendButton: 'Send',
  
  // Voice
  connecting: 'Connecting...',
  connected: 'Connected',
  listening: 'Listening...',
  speaking: 'Speaking...',
  
  // Loading
  processing: 'Processing...',
  generatingContent: 'Generating Content...',
  
  // Export
  exportTitle: 'Export Content',
  exportAsIs: 'Export As-Is',
  generateContent: 'Generate Content',
  downloadPDF: 'Download PDF',
  copyContent: 'Copy Content',
  close: 'Close',
  cancel: 'Cancel',
  
  // Errors
  connectionError: 'Connection failed. Please try again.',
  authError: 'Authentication failed. Please check your credentials.',
  generalError: 'An error occurred. Please try again.',
  
  // Status
  queryingDatabase: 'Querying database...',
  thinking: 'Thinking...',
  
  // Quick Actions
  followupQuestionsLabel: '💡 Follow-up questions:',
  suggestedTopicsLabel: '📋 Suggested topics:',
  
  // Content Display
  generatedContentTitle: 'Generated Content',
  sourceLinksTitle: 'Source Links',
  dataVisualizationTitle: 'Data Visualization'
};

/**
 * Government API Query Configuration
 */
export interface GovQueryConfig {
  campaignId: string;
  agentCode: string;
  generateContentAgentCode: string;
}

export const defaultGovQueryConfig: GovQueryConfig = {
  campaignId: '68d7fe2baf140df2048e51a5',
  agentCode: 'QUERY_DOCUMENTS_AND_RESPOND',
  generateContentAgentCode: 'GENERATE_CONTENT_FROM_CONVERSATION'
};

/**
 * Complete Chatbot Configuration
 */
export interface ChatbotConfig {
  api: APIConfig;
  theme: ThemeConfig;
  export: ExportConfig;
  voice: VoiceConfig;
  govQuery: GovQueryConfig;
}

export const defaultChatbotConfig: ChatbotConfig = {
  api: defaultAPIConfig,
  theme: defaultThemeConfig,
  export: defaultExportConfig,
  voice: defaultVoiceConfig,
  govQuery: defaultGovQueryConfig
};

